import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { History, Location, MemoryHistory } from 'history';

export type LeaveGuard = {
  /** Unblocks synchronously: a post-send redirect or a confirmed leave. */
  release: () => void;
  /** An in-app leave is being held. */
  pending: boolean;
  /** Drops the held leave. */
  stay: () => void;
  /**
   * release() then goes to the held location: a held Back/Forward moves through history,
   * a held push replaces this entry so Back skips it.
   */
  leave: () => void;
};

const cancelUnload = (event: BeforeUnloadEvent) => {
  event.preventDefault();
  event.returnValue = '';
};

// history v4 undoes a held Back with go(delta), but its delta is 0 for an entry from before this page load.
// Reports the go() that redoes the held move, so leaving keeps the wizard's entry ahead of the user.
const keepAddress = (history: History, redo: (steps: number) => void) => {
  const native = window.history;
  const nativeGo = native.go;
  let undone = 0;
  native.go = delta => {
    undone = delta ?? 0;
    nativeGo.call(native, delta);
  };
  queueMicrotask(() => {
    native.go = nativeGo;
    if (undone) return redo(-undone);
    const href = history.createHref(history.location);
    const { pathname, search, hash } = window.location;
    if (pathname + search + hash === href) return undefined;
    native.pushState({ key: history.location.key, state: history.location.state }, '', href);
    return redo(-1);
  });
};

// A memory history (specs) never moves while held, so the move is measured on its entries.
const isMemory = (history: History): history is MemoryHistory => 'entries' in history;

export const useLeaveGuard = (when: boolean): LeaveGuard => {
  const history = useHistory();
  const [held, setHeld] = useState<Location | null>(null);
  const releaseRef = useRef<() => void>(() => undefined);
  // A held Back/Forward is redone with go(), never push(), or Back would land on the wizard again.
  const redoRef = useRef<number | null>(null);

  useEffect(() => {
    if (!when) return undefined;
    // Returning false blocks without calling getUserConfirmation, so the router needs no change.
    const unblock = history.block((location, action) => {
      setHeld(location);
      redoRef.current = null;
      if (action !== 'POP') return false;
      if (isMemory(history)) {
        const to = history.entries.indexOf(location);
        if (to >= 0) redoRef.current = to - history.index;
      } else {
        keepAddress(history, steps => {
          redoRef.current = steps;
        });
      }
      return false;
    });
    window.addEventListener('beforeunload', cancelUnload);
    const release = () => {
      unblock();
      window.removeEventListener('beforeunload', cancelUnload);
    };
    releaseRef.current = release;
    return release;
  }, [history, when]);

  const release = useCallback(() => releaseRef.current(), []);
  const stay = useCallback(() => {
    redoRef.current = null;
    setHeld(null);
  }, []);
  const leave = useCallback(() => {
    const redo = redoRef.current;
    redoRef.current = null;
    releaseRef.current();
    setHeld(null);
    if (redo) history.go(redo);
    else if (held) history.replace(held);
  }, [history, held]);

  return useMemo(
    () => ({ release, pending: held !== null, stay, leave }),
    [release, held, stay, leave],
  );
};
